import { apiClient } from '../lib/apiClient';

// Mirrors backend/src/routes/care-team-auth.routes.ts

export type CareTeamRoleType =
  | 'dso_coordinator'
  | 'passport_funding'
  | 'respite_funding'
  | 'community_outreach'
  | 'music_therapy'
  | 'respite_worker'
  | 'other';

export type CareTeamProfileDto = {
  id: string;
  name: string;
  organization: string;
  roleType: CareTeamRoleType;
  credentials: string;
  phone: string;
  serviceArea: string;
  bio: string;
  address: string;
  logo: string;
  isDiscoverable: boolean;
  email?: string;
};

export type LoginResponse = {
  token: string;
  role: 'care_team';
  profile: CareTeamProfileDto;
};

export const authApi = {
  login: (email: string, password: string) =>
    apiClient.post<LoginResponse>('/care-team-auth/login', { email, password }),

  me: () => apiClient.get<CareTeamProfileDto>('/care-team-auth/me'),

  roles: () =>
    apiClient.get<{ value: CareTeamRoleType; label: string }[]>('/care-team-auth/roles'),

  forgotPassword: (email: string) =>
    apiClient.post<{ message: string }>('/care-team-auth/forgot-password', { email }),

  resetPassword: (email: string, code: string, newPassword: string) =>
    apiClient.post<{ message: string }>('/care-team-auth/reset-password', {
      email,
      code,
      newPassword,
    }),

  updateMe: (patch: Partial<Omit<CareTeamProfileDto, 'id' | 'email'>>) =>
    apiClient.patch<CareTeamProfileDto>('/care-team-auth/me', patch),

  changePassword: (currentPassword: string, newPassword: string) =>
    apiClient.post<{ success: true }>('/care-team-auth/change-password', {
      currentPassword,
      newPassword,
    }),
};

// ── Dashboard ────────────────────────────────────────────────────────────────

export type DashboardResponse = {
  stats: {
    totalClients: number;
    upcomingShifts: number;
    pendingInvoices: number;
  };
  clients: Array<{
    connectionId: string;
    householdId: string;
    youthSnapshot: { name?: string; dob?: string } | null;
    connectedAt: string | null;
    caregiverEmail: string;
    nextEvent: { id: string; title: string; date: string; time: string } | null;
  }>;
  upcomingEvents: Array<{
    id: string;
    connectionId: string;
    youthName: string;
    title: string;
    date: string;
    time: string;
    location: string;
    status: string;
  }>;
  recentInvoices: Array<{
    id: string;
    connectionId: string;
    invoiceNumber: string;
    totalAmount: number;
    currency: string;
    status: string;
    dueDate: string | null;
    createdAt: string;
  }>;
};

export const dashboardApi = {
  get: () => apiClient.get<DashboardResponse>('/care-team-auth/dashboard'),
};
